/**
 * Wallet Page
 * Manage StarCoins balance, add coins, and view transaction history
 * iOS-first design with consistent styling
 * 
 * @author Varun Pandey
 */

import React, { useState } from 'react';
import { Eye, EyeOff, Wallet, Plus, ArrowDownLeft, Hash, Calendar, CreditCard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import { useWalletBalance, useWalletHistory, WALLET_KEYS } from '@/api/hooks/useWallet';
import { WalletService } from '@/services/walletService';
import { useAuthStore } from '@/stores/authStore';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import { useQueryClient } from '@tanstack/react-query';
import { Loader } from '@/components/ui';

const HISTORY_PAGE_SIZE = 10;

export const WalletPage: React.FC = () => {
  const [balanceVisible, setBalanceVisible] = useState(false);
  const [starCoinsAmount, setStarCoinsAmount] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);

  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const fetchArtistDetails = useArtistDetailsStore((state) => state.fetchArtistDetails);
  const artistDetails = useArtistDetailsStore((state) => state.data);
  const ensureArtistDetailsFetched = useArtistDetailsStore((state) => state.ensureFetched);
  const { data: balanceResponse } = useWalletBalance();
  const { data: historyResponse, isLoading: isHistoryLoading } = useWalletHistory(currentPage);

  const [isProcessing, setIsProcessing] = useState(false);

  const walletBalance = balanceResponse?.data?.balance ?? 0;
  const transactions = historyResponse?.results ?? [];

  const quickAmounts = [500, 1000, 1500, 2000];

  /** Comma only for 5-digit amounts: 5000 → 5000, 50000 → 50,000 */
  const formatIndianRupee = (raw: string): string => {
    const digits = raw.replace(/\D/g, '');
    if (digits === '') return '';
    if (digits.length < 5) return digits;
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handleQuickAmountClick = (amount: number) => {
    const raw = amount.toString();
    setStarCoinsAmount(raw.length <= 5 ? raw : raw.slice(0, 5));
    if (errors.amount) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.amount;
        return newErrors;
      });
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 5);
    setStarCoinsAmount(raw);
    if (errors.amount) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.amount;
        return newErrors;
      });
    }
  };

  const handleAddStarCoins = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    // Wallet top-up is allowed only when profile is Live (or Approved).
    const details = artistDetails ?? (await ensureArtistDetailsFetched());
    const status = (details?.user_info?.profile_status ?? '').trim().toLowerCase();
    const isLiveStatus = status === 'live' || status === 'approved';
    const isIncomplete = status === 'incomplete';
    const isOnReview = status === 'on review' || status === 'on_review' || status === 'pending';

    if (isIncomplete) {
      toast.error('Please complete your profile to add wallet balance.');
      return;
    }
    if (isOnReview) {
      toast.error('Your profile is currently under review. You can add wallet balance once it goes live.');
      return;
    }
    if (!isLiveStatus) {
      toast.error('Your profile is not live yet. Please wait for it to go live before adding wallet balance.');
      return;
    }

    if (!starCoinsAmount.trim()) {
      newErrors.amount = 'Please enter an amount';
      toast.error('Please enter an amount to add StarCoins');
      setErrors(newErrors);
      return;
    }

    const amount = parseInt(starCoinsAmount, 10);
    if (isNaN(amount) || amount <= 0) {
      newErrors.amount = 'Please enter a valid amount';
      toast.error('Please enter a valid amount');
      setErrors(newErrors);
      return;
    }

    if (amount < 500) {
      newErrors.amount = 'Minimum recharge is 500 StarCoins';
      toast.error('Minimum recharge is 500 StarCoins');
      setErrors(newErrors);
      return;
    }

    if (amount > 50000) {
      newErrors.amount = 'Maximum recharge is 50,000 StarCoins';
      toast.error('Maximum recharge is 50,000 StarCoins');
      setErrors(newErrors);
      return;
    }

    if (amount % 50 !== 0) {
      newErrors.amount = 'Amount must be a multiple of 50 (e.g. 50, 100, 500, 1000, 5000)';
      toast.error('Amount must be a multiple of 50 (e.g. 50, 100, 500, 1000, 5000)');
      setErrors(newErrors);
      return;
    }

    setIsProcessing(true);
    const loadingToast = toast.loading('Initiating payment...');

    try {
      await WalletService.startTopUpCheckout({
        amount,
        customerName: user?.name || 'Artist',
        customerEmail: user?.email || '',
        customerPhone: user?.phone || '',
        onSuccess: async () => {
          toast.dismiss(loadingToast);
          toast.success(`${amount.toLocaleString()} StarCoins added successfully!`);
          queryClient.invalidateQueries({ queryKey: WALLET_KEYS.all });
          await fetchArtistDetails();
          setStarCoinsAmount('');
          setErrors({});
          setIsProcessing(false);
        },
        onFailure: (error) => {
          toast.dismiss(loadingToast);
          setIsProcessing(false);
          if (error.code === 'PAYMENT_CANCELLED') {
            toast.error('Payment cancelled');
          } else {
            toast.error(error.description || 'Payment failed');
          }
        },
      });

      toast.dismiss(loadingToast);
    } catch (error) {
      const err = error as { code?: string; message?: string };
      if (err.code === 'PAYMENT_CANCELLED') {
        toast.error('Payment cancelled');
      } else {
        toast.error(err.message || 'Could not initiate payment. Please try again.');
      }
      setIsProcessing(false);
      console.error('Payment initiation error:', error);
    }
  };

  // The API returns already formatted date like "03 October 06:18 PM"
  // Keep it as a reminder if needed, or remove if truly unused
  // const formatDate = (dateString: string) => dateString;

  return (
    <div className="w-full md:px-4 py-10 pb-24">
      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Wallet className="h-8 w-8 lg:h-10 lg:w-10 text-[#8E8E93]" />
          <h1 className="text-3xl lg:text-4xl font-bold text-white tracking-tight tracking-wide">Wallet</h1>
        </div>
        <p className="text-[#8E8E93] text-sm lg:text-base">
          Manage your StarCoins balance and transactions
        </p>
      </div>



      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Balance & Add Coins */}
        <div className="lg:col-span-5 space-y-6 lg:space-y-8">
          {/* Wallet Balance Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="relative overflow-hidden bg-gradient-to-br from-[#1C1C1E] via-[#1C1C1E] to-[#F33363]/10 rounded-[2.5rem]  p-6 lg:p-10 shadow-2xl"
          >
            {/* Decorative elements */}
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#F33363]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-[#2C2C2E] flex items-center justify-center ">
                    <Wallet className="h-5 w-5 text-[#8E8E93]" />
                  </div>
                  <span className="text-sm font-bold text-[#8E8E93] uppercase tracking-widest">Available Balance</span>
                </div>
                <button
                  onClick={() => setBalanceVisible(!balanceVisible)}
                  className="p-2.5 rounded-xl bg-[#2C2C2E]/80 backdrop-blur-sm  hover:border-[#F33363]/50 transition-all active:scale-95"
                  aria-label={balanceVisible ? 'Hide balance' : 'Show balance'}
                >
                  {balanceVisible ? (
                    <Eye className="h-5 w-5 text-[#8E8E93]" />
                  ) : (
                    <EyeOff className="h-5 w-5 text-[#8E8E93]" />
                  )}
                </button>
              </div>

              <div className="flex flex-col gap-1">
                <AnimatePresence mode="wait">
                  {balanceVisible ? (
                    <motion.div
                      key="visible"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex items-baseline gap-3"
                    >
                      <span className="text-5xl lg:text-7xl font-black text-white tracking-tighter">
                        {walletBalance.toLocaleString()}
                      </span>
                      <div className="flex flex-col">
                        <span className="text-xl font-bold text-[#F33363]">StarCoins</span>
                        <span className="text-[10px] font-black uppercase text-[#8E8E93] tracking-widest">Digital Asset</span>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.span
                      key="hidden"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="text-5xl lg:text-7xl font-black text-[#3A3A3C] tracking-tighter"
                    >
                      ••••••
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>

              <div className="mt-10 flex items-center gap-4 py-4 px-6 rounded-3xl bg-[#2C2C2E]/40  backdrop-blur-sm">
                <div className="p-2 rounded-full bg-emerald-500/10">
                  <ArrowDownLeft className="h-4 w-4 text-emerald-500" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-white tracking-wide">+2,400 this month</span>
                  <span className="text-[10px] text-[#8E8E93] uppercase font-bold tracking-widest">Earnings overview</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Add StarCoins Section */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            onSubmit={handleAddStarCoins}
            className="border border-[#1C1C1E] rounded-[2rem]  p-6 lg:p-10"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-[#F33363]/10 flex items-center justify-center ">
                <Plus className="h-6 w-6 text-[#F33363]" />
              </div>
              <h2 className="text-2xl font-black text-white tracking-tight">Top Up</h2>
            </div>

            <div className="space-y-8">
              <div className="group">
                <label className="mb-3 block text-[10px] font-black text-[#8E8E93] uppercase tracking-[0.2em]">
                  Enter Amount
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 flex items-center gap-2 pointer-events-none">
                    ₹
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatIndianRupee(starCoinsAmount)}
                    onChange={handleAmountChange}
                    placeholder="Enter amount"
                    className={cn(
                      'w-full pl-12 pr-4 py-5 bg-[#2C2C2E]/50 border rounded-2xl text-white placeholder-[#8E8E93]',
                      'focus:outline-none focus:ring-2 focus:ring-[#F33363]/30 focus:border-[#F33363]',
                      'text-xl font-black transition-all tracking-tight',
                      errors.amount ? 'border-red-500' : 'border-[#3A3A3C] hover:border-[#F33363]/50',
                    )}
                  />
                </div>
                {errors.amount && (
                  <p className="mt-2 text-xs font-bold text-red-500 flex items-center gap-1">
                    <span>•</span> {errors.amount}
                  </p>
                )}
              </div>

              {/* Quick Selection */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[10px] font-black text-[#8E8E93] uppercase tracking-[0.2em]">Quick Selection</p>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-500 text-[8px] font-black uppercase">Most Popular</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {quickAmounts.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => handleQuickAmountClick(amount)}
                      className={cn(
                        'p-4 rounded-2xl border transition-all text-sm font-black tracking-tight flex flex-col items-start gap-1 group/q min-h-[4.5rem]',
                        starCoinsAmount === amount.toString()
                          ? 'bg-[#F33363] border-[#F33363] text-white shadow-lg shadow-[#F33363]/20'
                          : 'bg-[#2C2C2E]/30 border-[#3A3A3C] text-[#8E8E93] hover:border-[#F33363]/50 hover:text-white',
                      )}
                    >
                      <span className="flex w-full items-center justify-between">
                        <span>{amount.toLocaleString()} StarCoins</span>
                        {starCoinsAmount === amount.toString() ? (
                          <div className="bg-white/20 p-1 rounded-full">
                            <Plus className="h-3 w-3 text-white" />
                          </div>
                        ) : (
                          <div className="bg-[#3A3A3C] group-hover/q:bg-[#F33363]/20 p-1 rounded-full transition-colors">
                            <Plus className="h-3 w-3 text-[#8E8E93] group-hover/q:text-[#F33363]" />
                          </div>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                variant="active"
                className="w-full py-5 rounded-2xl text-lg font-black tracking-tight shadow-xl shadow-[#F33363]/20"
                disabled={!starCoinsAmount || !!errors.amount || isProcessing}
              >
                {isProcessing ? (
                  <div className="flex items-center gap-2">
                    <Loader size="small" />
                    Processing...
                  </div>
                ) : (
                  <>
                    <Plus className="h-6 w-6" />
                    Continue to Payment
                  </>
                )}
              </Button>
            </div>
          </motion.form>
        </div>

        {/* Right Column: Transaction History */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="lg:col-span-7 bg-[#101013] rounded-[2.25rem] border border-[#252528] flex flex-col overflow-hidden h-full min-h-[520px]"
        >
          <div className="px-6 lg:px-8 py-5 lg:py-6 border-b border-[#252528] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-2xl bg-[#18181A] flex items-center justify-center border border-[#2E2E31]">
                <Hash className="h-4 w-4 text-[#8E8E93]" />
              </div>
              <div className="flex flex-col">
                <h2 className="text-base lg:text-lg font-semibold text-white tracking-tight">
                  Recent Activity
                </h2>
                <p className="text-[11px] text-[#8E8E93]">
                  Wallet transactions and StarCoins movement
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col overflow-y-auto px-4 lg:px-6 py-4 space-y-3 custom-scrollbar">
            {transactions.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-20">
                <div className="w-20 h-20 rounded-full bg-[#2C2C2E] flex items-center justify-center mb-6">
                  <Wallet className="h-10 w-10 text-[#3A3A3C]" />
                </div>
                <p className="text-[#8E8E93] text-lg font-bold">
                  {isHistoryLoading ? <Loader size='small' /> : 'No transactions found'}
                </p>
                {!isHistoryLoading && (
                  <p className="text-[#8E8E93] text-sm mt-1 max-w-xs">
                    Once you start using StarCoins, your history will appear here.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2.5">
                {transactions.map((transaction, index) => {
                  const isCredit = transaction.transaction_type === 1;
                  const isFailed = transaction.transaction_status === -1;
                  const coinsAmount = Math.floor(transaction.coins);

                  return (
                    <motion.div
                      key={`${currentPage}-${index}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className={cn(
                        'relative rounded-2xl bg-[#151516] px-4 py-3 border border-transparent transition-colors duration-150',
                        !isFailed && 'hover:border-[#2E2E31]',
                        isFailed && 'opacity-60'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {/* Left stripe for quick glance */}
                        <div
                          className={cn(
                            'h-10 w-0.5 rounded-full',
                            isFailed
                              ? 'bg-[#4B1F24]'
                              : isCredit
                                ? 'bg-emerald-500/70'
                                : 'bg-red-500/70'
                          )}
                        />

                        {/* Main content */}
                        <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-white truncate">
                              {transaction.transaction_message}
                            </p>
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#8E8E93]">
                              <span className="inline-flex items-center gap-1.5">
                                <Calendar className="h-3 w-3" />
                                <span>{transaction.created_at_formatted}</span>
                              </span>
                              {transaction.transaction_id && (
                                <span className="inline-flex items-center gap-1.5">
                                  <CreditCard className="h-3 w-3" />
                                  <span className="truncate max-w-[140px] lg:max-w-[220px]">
                                    {transaction.transaction_id}
                                  </span>
                                </span>
                              )}
                              {isFailed && (
                                <span className="inline-flex items-center rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-400">
                                  Failed
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-col items-end flex-shrink-0">
                            <span
                              className={cn(
                                'text-sm lg:text-base font-semibold tabular-nums',
                                isFailed
                                  ? 'text-[#6B6B6E] line-through'
                                  : isCredit
                                    ? 'text-emerald-400'
                                    : 'text-red-400'
                              )}
                            >
                              {isCredit ? '+' : '-'}
                              {coinsAmount.toLocaleString()}
                            </span>
                            <span className="mt-0.5 text-[10px] font-medium uppercase tracking-widest text-[#8E8E93]">
                              SC
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}

                {/* Pagination: match Jobs page style (sliding window, Prev/Next, spinners) */}
                {historyResponse && historyResponse.count > 0 && (() => {
                  const totalPages = Math.ceil(historyResponse.count / HISTORY_PAGE_SIZE);
                  if (totalPages <= 1) return null;

                  const WINDOW_SIZE = 5;
                  let startPage = 1;
                  let endPage = totalPages;

                  if (totalPages > WINDOW_SIZE) {
                    if (currentPage <= 3) {
                      startPage = 1;
                      endPage = WINDOW_SIZE;
                    } else if (currentPage >= totalPages - 2) {
                      endPage = totalPages;
                      startPage = totalPages - (WINDOW_SIZE - 1);
                    } else {
                      startPage = currentPage - 2;
                      endPage = currentPage + 2;
                    }
                  } else {
                    startPage = 1;
                    endPage = totalPages;
                  }

                  const pagesToRender: number[] = [];
                  for (let page = startPage; page <= endPage; page += 1) {
                    pagesToRender.push(page);
                  }

                  const scrollListToTop = () => {
                    const container = document.querySelector('.custom-scrollbar');
                    if (container instanceof HTMLElement) {
                      container.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  };

                  return (
                    <div className="flex items-center justify-center gap-2 pt-4 pb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setCurrentPage((p) => {
                            const next = Math.max(1, p - 1);
                            return next;
                          });
                          scrollListToTop();
                        }}
                        disabled={currentPage <= 1 || isHistoryLoading}
                        className={cn(
                          'flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors min-w-[4.5rem]',
                          currentPage <= 1 || isHistoryLoading
                            ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                            : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]'
                        )}
                        aria-label="Previous page"
                      >
                        {isHistoryLoading ? (
                          <Loader size="small" />
                        ) : (
                          <>
                            <span className="text-xs">Prev</span>
                          </>
                        )}
                      </button>
                      <div className="flex items-center gap-1">
                        {pagesToRender.map((page) => (
                          <button
                            key={page}
                            type="button"
                            onClick={() => {
                              setCurrentPage(page);
                              scrollListToTop();
                            }}
                            disabled={isHistoryLoading}
                            className={cn(
                              'min-w-[2.25rem] h-9 px-2 rounded-lg text-sm font-bold border transition-colors flex items-center justify-center',
                              page === currentPage
                                ? 'border-[#F33363] bg-[#F33363] text-white'
                                : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]',
                              isHistoryLoading && 'opacity-70 cursor-not-allowed'
                            )}
                            aria-label={`Page ${page}`}
                            aria-current={page === currentPage ? 'page' : undefined}
                          >
                            {page === currentPage && isHistoryLoading ? (
                              <Loader size="small" />
                            ) : (
                              page
                            )}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setCurrentPage((p) => {
                            const next = Math.min(totalPages, p + 1);
                            return next;
                          });
                          scrollListToTop();
                        }}
                        disabled={currentPage >= totalPages || isHistoryLoading}
                        className={cn(
                          'flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors min-w-[4.5rem]',
                          currentPage >= totalPages || isHistoryLoading
                            ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                            : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]'
                        )}
                        aria-label="Next page"
                      >
                        {isHistoryLoading ? (
                          <Loader size="small" />
                        ) : (
                          <>
                            <span className="text-xs">Next</span>
                          </>
                        )}
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
